import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { TypeMenu } from "@/Models/Menu";
import { useLocation, useNavigate } from "react-router-dom";
import { UseStateCall } from "@/utility-types";
import { useQuery } from "@tanstack/react-query";
import { UsuarioService } from "@/Services/UsuarioService";
import { useSessao } from "./SessaoContext";

type MenuSelecionadoState = UseStateCall<TypeMenu | null>;

type Navegacao = {
    ultimasRotas: string[];
    menusDisponiveis: TypeMenu[] | null
    menuSelecionado: MenuSelecionadoState[0];
    setMenuSelecionado: MenuSelecionadoState[1];
};

type TypeNavegacaoContext = Navegacao;

const NavegacaoContext = createContext<TypeNavegacaoContext | null>(null);

export const NavegacaoContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [ultimasRotas, setUltimasRotas] = useState<string[]>([]);
    const [menuSelecionado, setMenuSelecionado] = useState<TypeMenu | null>(null);

    const { pathname, search } = useLocation();
    const { sessao } = useSessao();

    //#region query / set sessao.menus
    const {data: menusDisponiveis, ...menusQuery} = useQuery({
        queryKey: ["listarMenuDoUsuario", sessao?.token],
        queryFn: () => new UsuarioService().listarMenu(),
        enabled: sessao?.token != undefined,
        gcTime: Infinity,
        select: (r) => r.data,
    });

    useEffect(() => {
        const url = new URL(window.location.href);
        const path = url.pathname + url.search;
        if (path === ultimasRotas[ultimasRotas.length - 1]) return;

        let r = [...ultimasRotas, path];
        r.reverse();
        r = r.slice(0, 100); // limite de 100 rotas
        r.reverse();

        setUltimasRotas(r);
    }, [pathname, search]);

    const value: TypeNavegacaoContext = {
        ultimasRotas,
        menuSelecionado,
        setMenuSelecionado,
        menusDisponiveis: menusDisponiveis ?? null
    };

    return <NavegacaoContext.Provider value={value}>{children}</NavegacaoContext.Provider>;
};

export const useMenuSelecionado = () => {
    const navegação = useContext(NavegacaoContext);

    if (navegação == null) {
        throw new Error("NavegacaoContext não encontrado");
    }

    const {menuSelecionado, setMenuSelecionado} = navegação;
    return { menuSelecionado, setMenuSelecionado };
}

export const useUltimasRotas = () => {
    const navegação = useContext(NavegacaoContext);

    if (navegação == null) {
        throw new Error("NavegacaoContext não encontrado");
    }

    return navegação.ultimasRotas;
}

export const useMenusDisponiveis = () => {
    const navegação = useContext(NavegacaoContext);
    if (navegação == null) {
        throw new Error("NavegacaoContext não encontrado");
    }

    return navegação.menusDisponiveis;
}

export const useVoltar = (fallback: string = "..") => {
    const navigate = useNavigate();
    const ultimasRotas = useUltimasRotas();

    const voltar = useCallback(() => {
        if(ultimasRotas.length === 0) return navigate(fallback);
        if(ultimasRotas.length === 1) return navigate(fallback);

        const to = ultimasRotas[ultimasRotas.length - 2];
        console.log(to);

        return navigate(to);
    }, [ultimasRotas, navigate]);

    return voltar;
}